/* eslint-disable local/no-localstorage-in-react, no-restricted-syntax --
 * FT-365 v3 R2 blocker 8: this modal is the UI counterpart to Layer A#1's
 * `sr.vessel.showPickerGate` flag. Direct LS access is required — the flag
 * itself lives in localStorage and is written by AuthGate BEFORE VesselContext
 * has hydrated, so we read/write LS keys here rather than via useVessel/useStorageState.
 */
/**
 * FT-365 v3 R2 blocker 8: VesselPickerModal.
 *
 * Purpose: Layer A#1 (AuthGate reconciliation) sets `sr.vessel.showPickerGate=true`
 * when all three fallbacks (preferredVesselId → previous currentVesselId →
 * serverVessels[0]) fail to yield a valid vessel. Before this modal existed,
 * `currentVesselId=null` propagated to downstream modules which either crashed
 * on `currentVessel.id` or rendered blank. This modal reads the flag and:
 *
 *   - If localStorage.vessels is EMPTY (or absent): render a blocking message
 *     "No vessels available for your account. Contact your administrator."
 *     No close button, no dismiss — user cannot proceed without a vessel.
 *
 *   - If localStorage.vessels has entries: render a radio list of vessels
 *     with a "Select vessel" button. On select:
 *       1. legacySet(null, 'currentVesselId', selectedId)
 *       2. localStorage.removeItem('sr.vessel.showPickerGate')
 *       3. dispatch `sr:vessel:selected` event so VesselContext picks it up
 *
 *   - If the flag is not set: renders nothing (null return).
 *
 * Mounted in TailwindShell alongside other overlays. Reads the flag on mount
 * and via a `storage` listener + custom `sr:vessel:pickerGate:changed` event
 * so late writes trigger re-render.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Ship, AlertCircle } from 'lucide-react';
// FT-365 v3.1 hotfix: legacySet removed — see handleSelect comment below.
// currentVesselId is written raw so downstream readers (which all use raw
// localStorage.getItem) can match the UUID.

const GATE_KEY = 'sr.vessel.showPickerGate';
const VESSELS_KEY = 'vessels';
const CURRENT_VESSEL_KEY = 'currentVesselId';

function readGate() {
  try {
    return localStorage.getItem(GATE_KEY) === 'true';
  } catch (_) {
    return false;
  }
}

function readVessels() {
  try {
    const raw = localStorage.getItem(VESSELS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v) => v && v.id);
  } catch (_) {
    return [];
  }
}

const VesselPickerModal = () => {
  const [showGate, setShowGate] = useState(() => readGate());
  const [vessels, setVessels] = useState(() => readVessels());
  const [selectedId, setSelectedId] = useState(null);

  const refresh = useCallback(() => {
    setShowGate(readGate());
    setVessels(readVessels());
  }, []);

  useEffect(() => {
    // React to LS changes (cross-tab storage event, plus in-tab custom event
    // dispatched by AuthGate when the gate flag flips).
    const onStorage = (e) => {
      if (!e || !e.key) { refresh(); return; }
      if (e.key === GATE_KEY || e.key === VESSELS_KEY) refresh();
    };
    const onGateChanged = () => refresh();
    window.addEventListener('storage', onStorage);
    window.addEventListener('sr:vessel:pickerGate:changed', onGateChanged);
    // Also poll shortly after mount — AuthGate may set the flag between
    // this component's mount and its first storage-event listener install.
    const t = setTimeout(refresh, 250);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('sr:vessel:pickerGate:changed', onGateChanged);
      clearTimeout(t);
    };
  }, [refresh]);

  if (!showGate) return null;

  const handleSelect = () => {
    if (!selectedId) return;
    try {
      // FT-365 v3.1 hotfix: raw write (not legacySet). legacySet routes
      // through JSON.stringify and double-encoded the UUID, causing the
      // FT-64 vessel-guard to reject every subsequent vessel-scoped write
      // (54k warnings in 30s → UI freeze in prod). Raw setItem matches
      // what every downstream reader (vesselGuard.js:105, VesselContext.js,
      // DefectRecordModal.js:18, EquipmentDetailView.js:56, etc.) expects.
      localStorage.setItem(CURRENT_VESSEL_KEY, selectedId);
    } catch (_) { /* non-fatal */ }
    try {
      localStorage.removeItem(GATE_KEY);
    } catch (_) { /* non-fatal */ }
    try {
      window.dispatchEvent(new CustomEvent('sr:vessel:selected', {
        detail: { vesselId: selectedId, source: 'FT-365 v3 picker' },
      }));
    } catch (_) { /* non-fatal */ }
    setShowGate(false);
    // FT-365 v3 R3: no listener exists for sr:vessel:selected, so VesselContext
    // React state would stay null until reload. Match the LoginScreen post-auth
    // pattern and reload — the picker only fires when all Layer A#1 fallbacks
    // failed, so a reload UX cost is acceptable and consistent.
    window.location.reload();
  };

  const emptyVessels = vessels.length === 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ft365-vessel-picker-title"
      data-testid="ft365-vessel-picker-modal"
    >
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
        {emptyVessels ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-8 w-8 text-amber-600" />
            </div>
            <h2
              id="ft365-vessel-picker-title"
              className="text-xl font-semibold text-gray-900 mb-2"
            >
              No vessels available
            </h2>
            <p className="text-gray-600 text-sm">
              No vessels available for your account. Contact your administrator.
            </p>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                <Ship className="h-6 w-6 text-cyan-600" />
              </div>
              <div>
                <h2
                  id="ft365-vessel-picker-title"
                  className="text-xl font-semibold text-gray-900"
                >
                  Select a vessel
                </h2>
                <p className="text-gray-500 text-sm">
                  Pick a vessel to continue.
                </p>
              </div>
            </div>
            <ul className="space-y-2 max-h-64 overflow-y-auto mb-6">
              {vessels.map((v) => (
                <li key={v.id}>
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="ft365-vessel-picker"
                      value={v.id}
                      checked={selectedId === v.id}
                      onChange={() => setSelectedId(v.id)}
                      className="h-4 w-4 text-cyan-600"
                      data-testid={`ft365-vessel-picker-option-${v.id}`}
                    />
                    <span className="text-gray-900">{v.name || v.id}</span>
                  </label>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={handleSelect}
              disabled={!selectedId}
              className={`w-full py-2 px-4 rounded-lg text-white font-medium ${
                selectedId ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-gray-300 cursor-not-allowed'
              }`}
              data-testid="ft365-vessel-picker-select"
            >
              Select vessel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VesselPickerModal;
